import React, { useState, useMemo } from 'react';
import { IFilterOptions } from 'interfaces/SelectOptions';
import { useQuery } from 'react-query';
import { postSerialNumbers } from 'services/prediction/serial-number';
import { getCluster } from 'services/cluster';
import MultipleCheckboxWithSubgroup from 'components/atoms/MultipleCheckboxWithSubgroup';
import { useDispatchFilter, useSelectFilter } from 'hooks/planning/useFilter';
import FilterBarItem from 'components/molecules/FilterBarItem';
import { Grid } from '@material-ui/core';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import DateDayjstUtils from '@date-io/dayjs';
import ptBR from 'dayjs/locale/pt-br';
import dayjs from 'dayjs';
import useStyles from './style';

const PredictionFilterContainer = () => {
  const classes = useStyles();
  const changeFilter = useDispatchFilter();

  const [
    serialNumberFilterOptionsContext,
    installationFilterOptionsContext,
    selectedInitialDateOptionContext,
    selectedFinalDateOptionContext,
  ] = [
    useSelectFilter('serial-number'),
    useSelectFilter('installation'),
    useSelectFilter('initialDate'),
    useSelectFilter('finalDate'),
  ];
  /** ESTADO DOS FILTROS */
  const [filterClusterOptions, setFilterClusterOptions] = useState<
    IFilterOptions[]
  >([]);

  const [filterSerialNumberOptions, setFilterSerialNumberOptions] = useState<
    IFilterOptions[]
  >(serialNumberFilterOptionsContext);

  const installationId = installationFilterOptionsContext.map(item =>
    String(item.value),
  );

  const pauseFilterSerialNumber = useMemo<boolean>(() => {
    return serialNumberFilterOptionsContext.length > 1;
  }, [serialNumberFilterOptionsContext]);

  /** QUERY DOS FILTROS */
  const queryFilterCluster = useQuery({
    queryKey: ['clusterFilterOptionsContext'],
    queryFn: async () => {
      const { data } = await getCluster();

      return data;
    },
    select: data => {
      return data.map(item => {
        return {
          label: item?.description,
          value: item?.id,
          children: item.installations.map(installation => {
            return {
              label: installation?.description,
              value: installation?.id,
            };
          }),
        };
      });
    },
    onSuccess: (data: IFilterOptions[]) => {
      setFilterClusterOptions(data);
    },
    enabled: true,
  });

  const queryFilterSerialNumber = useQuery({
    queryKey: [
      'serialNumberFilterOptionsContext',
      installationFilterOptionsContext,
    ],
    queryFn: async () => {
      const params = {
        installationId,
      };
      const { data } = await postSerialNumbers(params);
      return data;
    },

    select: data => {
      return data.map(item => {
        return {
          label: item?.serieNumber,
          value: item?.serieNumber,
        };
      });
    },
    onSuccess: (data: IFilterOptions[]) => {
      setFilterSerialNumberOptions(data);
    },
    enabled: !pauseFilterSerialNumber,
  });

  const minDate = React.useMemo(
    () => selectedInitialDateOptionContext[0]?.value,
    [selectedInitialDateOptionContext],
  );
  const initialDateMemo = React.useMemo(
    () => selectedInitialDateOptionContext[0]?.value,
    [selectedInitialDateOptionContext],
  );

  const finalDateMemo = React.useMemo(
    () => selectedFinalDateOptionContext[0]?.value,
    [selectedFinalDateOptionContext],
  );

  const handleChange = ({
    name,
    value,
  }: {
    name: string;
    value: IFilterOptions[];
  }) => {
    changeFilter(name, value);
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={1}>
        <Grid item xs={8}>
          <Grid container spacing={1}>
            <Grid item xs={3}>
              <MultipleCheckboxWithSubgroup
                items={filterClusterOptions}
                label="Cluster/Instalação"
                loading={queryFilterCluster.isLoading}
                selectAllLabel="Selecionar todos"
                id="cluster-installation"
                groupName="cluster"
                itemName="installation"
                noOptionsText="Nenhum resultado encontrado"
                onFilterChange={handleChange}
              />
            </Grid>

            <Grid item xs={2}>
              <FilterBarItem
                options={filterSerialNumberOptions}
                onFilterChange={handleChange}
                id="serial-number"
                name="serial-number"
                label="Nº de série"
                loading={queryFilterSerialNumber.isLoading}
                initialState={serialNumberFilterOptionsContext}
              />
            </Grid>
            <Grid item xs={3}>
              <MuiPickersUtilsProvider locale={ptBR} utils={DateDayjstUtils}>
                <KeyboardDatePicker
                  value={initialDateMemo}
                  onChange={date => {
                    if (dayjs(date).isValid()) {
                      handleChange({
                        name: 'initialDate',
                        value: [
                          {
                            label: dayjs(date).format('YYYY-MM-DD') || '',
                            value: dayjs(date).format('YYYY-MM-DD') || '',
                          },
                        ],
                      });
                    }
                  }}
                  inputVariant="outlined"
                  minDate={dayjs(new Date(1999, 12, 31))}
                  minDateMessage="Data inválida"
                  maxDateMessage="Os dados não devem ser posteriores à data final"
                  maxDate={finalDateMemo}
                  label="Data início"
                  cancelLabel="Cancelar"
                  format="DD/MM/YYYY"
                  className={classes.iconButton}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid item xs={3}>
              <MuiPickersUtilsProvider locale={ptBR} utils={DateDayjstUtils}>
                <KeyboardDatePicker
                  value={finalDateMemo}
                  onChange={date => {
                    if (dayjs(date).isValid()) {
                      handleChange({
                        name: 'finalDate',
                        value: [
                          {
                            label: dayjs(date).format('YYYY-MM-DD') || '',
                            value: dayjs(date).format('YYYY-MM-DD') || '',
                          },
                        ],
                      });
                    }
                  }}
                  minDate={minDate}
                  inputVariant="outlined"
                  label="Data fim"
                  minDateMessage="Os dados não devem ser anteriores à data inicial"
                  maxDateMessage="Data inválida"
                  cancelLabel="Cancelar"
                  format="DD/MM/YYYY"
                  className={classes.iconButton}
                />
              </MuiPickersUtilsProvider>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default PredictionFilterContainer;
